import * as yup from 'yup';
import { toast } from 'react-toastify';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Input } from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import request from 'Utils/Helper/Request';
import { Button, Field, ErrorNote } from 'Shared/UI';
import AuthLayout from './AuthLayout';

const registerSchema = yup.object().shape({
  name: yup.string().trim().required('Enter your company name.'),
  email: yup.string().trim().email('Enter a valid email address.').required('Enter your email address.'),
  phone: yup.string().trim().required('Enter a phone number.'),
  password: yup.string().min(8, 'Use at least 8 characters.').required('Choose a password.'),
  terms: yup.bool().oneOf([true], 'Accept the terms to continue.'),
});

const FIELDS = [
  { name: 'name', label: 'Company name', type: 'text', autoComplete: 'organization' },
  { name: 'email', label: 'Work email', type: 'email', autoComplete: 'email' },
  { name: 'phone', label: 'Phone number', type: 'tel', autoComplete: 'tel', placeholder: '0803 000 0000' },
];

const RegisterForm = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: { name: '', email: '', phone: '', password: '', terms: false },
    resolver: yupResolver(registerSchema),
  });

  const onSubmit = async (values) => {
    setError(null);
    try {
      const response = await request(`${process.env.REACT_APP_MTN_BASE_API_URL}/auth/register`, {
        method: 'POST',
        body: { name: values.name.trim(), email: values.email.trim(), phone: values.phone.trim(), password: values.password },
      });
      if (!response?.status) {
        setError(response?.message || 'We could not create the account.');
        return;
      }
      toast.success('Account created. Sign in to verify your company.', { position: 'top-right' });
      navigate(`${process.env.PUBLIC_URL}/auth/login`);
    } catch (err) {
      setError(err?.response?.payload?.message || 'Cannot reach the server. Check your connection and try again.');
    }
  };

  return (
    <AuthLayout
      title="Create a partner account"
      subtitle="After you sign in we'll ask for your company details (CAC, contact person, BVN or NIN and a utility bill). You can try the sandbox while we check them."
      footer={
        <>
          Already have an account? <Link to={`${process.env.PUBLIC_URL}/auth/login`}>Sign in</Link>
        </>
      }
    >
      <form className="au-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {FIELDS.map((f) => (
          <Controller
            key={f.name}
            name={f.name}
            control={control}
            render={({ field }) => (
              <Field label={f.label} htmlFor={f.name} error={errors[f.name]?.message}>
                <Input id={f.name} type={f.type} autoComplete={f.autoComplete} placeholder={f.placeholder} {...field} invalid={!!errors[f.name]} />
              </Field>
            )}
          />
        ))}
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <Field label="Password" htmlFor="password" hint="At least 8 characters." error={errors.password?.message}>
              <div className="au-pass">
                <Input id="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" {...field} invalid={!!errors.password} />
                <button type="button" onClick={() => setShowPassword((s) => !s)} aria-pressed={showPassword}>
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </Field>
          )}
        />
        <Controller
          name="terms"
          control={control}
          render={({ field }) => (
            <label className="au-check" htmlFor="terms">
              <input id="terms" type="checkbox" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
              <span>I agree to the terms of service and privacy policy.</span>
            </label>
          )}
        />
        {errors.terms && <p className="ed-field__error" style={{ marginTop: -4 }}>{errors.terms.message}</p>}
        <ErrorNote error={error} />
        <Button type="submit" busy={isSubmitting}>
          Create account
        </Button>
      </form>
    </AuthLayout>
  );
};

export default RegisterForm;
